import React from 'react';
import { AvatarItem } from '@atlaskit/avatar';
import { injectIntl, type WrappedComponentProps, type MessageDescriptor } from 'react-intl-next';
import { Title, Description, CardWrapper, PrimaryTextWrapper } from './styled';
import Tooltip from '@atlaskit/tooltip';
import ShortcutIcon from '@atlaskit/icon/core/migration/link-external--shortcut';
import { type UIAnalyticsEvent } from '@atlaskit/analytics-next';
import { AnalyticsEventToProps } from '@atlassian/analytics-bridge';

interface Props {
	name: MessageDescriptor;
	description?: MessageDescriptor;
	icon: React.ReactNode;
	onClick: (ev: React.MouseEvent, analyticsEvent?: UIAnalyticsEvent) => void;
	onMouseEnter?: (e: React.MouseEvent<HTMLDivElement>) => void;
	tooltip?: MessageDescriptor;
	isDisabled: boolean;
	isActive: boolean;
	renderShortcutIcon: boolean;
}

const TemplateCard = ({
	name,
	description,
	icon,
	intl,
	onClick,
	onMouseEnter,
	tooltip,
	isDisabled,
	isActive,
	renderShortcutIcon,
}: Props & WrappedComponentProps) => {
	const primaryText = (
		<PrimaryTextWrapper>
			<Title>{intl.formatMessage(name)}</Title>
			{renderShortcutIcon && (
				<ShortcutIcon color="currentColor" LEGACY_size="small" label="open in tab" />
			)}
		</PrimaryTextWrapper>
	);
	const card = (
		<CardWrapper
			onMouseEnter={isDisabled ? undefined : onMouseEnter}
			isActive={isActive}
			// eslint-disable-next-line @atlaskit/ui-styling-standard/no-classname-prop -- Ignored via go/DSP-18766
			className={isDisabled ? 'is-disabled' : ''}
			hasTooltip={!!tooltip}
			data-testid="confluence-project-pages.template-sidebar-card"
		>
			<AvatarItem
				avatar={icon}
				primaryText={primaryText}
				secondaryText={
					description ? <Description>{intl.formatMessage(description)}</Description> : undefined
				}
				onClick={onClick}
				testId="confluence-project-pages.templates-sidebar.template-card"
			/>
		</CardWrapper>
	);
	if (tooltip) {
		return <Tooltip content={intl.formatMessage(tooltip)}>{card}</Tooltip>;
	} else {
		return card;
	}
};

export default AnalyticsEventToProps('templateButton', {
	onClick: 'clicked',
	// @ts-ignore - the AnalyticsEventToProps HOC doesn't allow functional components in its typing but there is no reason for that
})(injectIntl(TemplateCard)) as React.FC<Props>;
